import { RefObject, useCallback, useLayoutEffect, useState } from 'react'
import { ImperativePanelHandle } from 'react-resizable-panels'

import { AI_PANEL_SIZE_TRANSITION_MS } from 'src/components/constants'
import { usePrefersReducedMotion } from 'src/hooks/usePrefersReducedMotion'

type PendingResize = {
    targetSize: number
}

export const useAiPanelSizeAnimation = (panelRef: RefObject<ImperativePanelHandle | null>) => {
    const [pendingResize, setPendingResize] = useState<PendingResize | null>(null)
    const prefersReducedMotion = usePrefersReducedMotion()

    /* react-resizable-panels sizes panels through inline flex values, so the transition class has to be
       committed before the size changes. A layout effect runs after the class is in the DOM and before paint. */
    useLayoutEffect(() => {
        if (pendingResize === null) {
            return
        }

        panelRef.current?.resize(pendingResize.targetSize)
        const timeout = window.setTimeout(() => setPendingResize(null), AI_PANEL_SIZE_TRANSITION_MS)

        return () => window.clearTimeout(timeout)
    }, [pendingResize, panelRef])

    const animateResize = useCallback(
        (targetSize: number) => {
            if (prefersReducedMotion) {
                setPendingResize(null)
                panelRef.current?.resize(targetSize)

                return
            }

            setPendingResize({ targetSize })
        },
        [panelRef, prefersReducedMotion],
    )

    const cancelAnimation = useCallback(() => setPendingResize(null), [])

    return { isAnimatingPanelSize: pendingResize !== null, animateResize, cancelAnimation }
}
